import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { observable, action } from 'mobx';
import { AnchorButton, Button, Icon, Intent, Spinner } from '@blueprintjs/core';
import moment from 'moment';
import { Api } from 'services/Api';
import Toast from 'helpers/toaster';
import { bookingReference } from 'helpers/bookingReference';
import { joinAddress } from 'components/Booking/BookingLayout';

// "Client and job" on the booking page (pages/bookings/show.js).
//
// 1. Client: existing clients with the booking's email or phone are offered
//    (POST /clients/matches); otherwise, or instead, create one from the
//    booking's details.
// 2. Location: pick one of the client's locations (the one matching the
//    booking's address is preselected) or add the booking's address.
// 3. Job: opens the job panel prefilled from the booking. Once the job
//    exists, its schedule and the booking's date stay in sync (backend).
//
// Bookings don't store a client: until the job exists, the choice lives here.
// Styles: styles/booking-form.scss (.bk-client-job).

const JOB_LENGTH_HOURS = 1;
const NEW_LOCATION = 'new';

const normalise = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();

// The street part of an address: "10 Ridgehill Drive, Brampton, ON" -> "10 ridgehill drive".
const street = (address) => normalise(String(address || '').split(',')[0]);

const sameAddress = (location, attributes) => {
  const target = street(attributes.address);
  return Boolean(target) && normalise(location.attributes.full_address).startsWith(target);
};

const customerName = (attributes) => [attributes.first_name, attributes.last_name].filter(Boolean).join(' ').trim();

const REASON_LABELS = { email: 'Same email', phone: 'Same phone' };

// The one-time job created from this booking, if any.
export const bookingJob = (attributes) => (
  ((attributes && attributes.scheduled_events) || []).find((event) => !event.service_agreement_id && !event.deleted_at) || null
);

export const isRecurringBooking = (attributes) => Boolean(
  attributes && attributes.frequency && attributes.frequency.title && attributes.frequency.title !== 'One Time'
);

@inject('bookingStore', 'jobStore', 'store') @observer
class BookingClientJob extends Component {

  @observable matches = null;
  @observable reasons = {};
  @observable client = null;
  @observable locations = [];
  @observable locationChoice = null;
  @observable busy = null;
  @observable jobClient = null;

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    const before = bookingJob(prevProps.attributes);
    const now = bookingJob(this.props.attributes);
    if ((before && before.id) !== (now && now.id) || (prevProps.attributes && prevProps.attributes.id) !== (this.props.attributes && this.props.attributes.id)) {
      this.load();
    }
  }

  load() {
    const { attributes } = this.props;
    if (!attributes) return;
    const job = bookingJob(attributes);
    if (job) {
      this.loadJobClient(job);
    } else if (!this.client) {
      this.findMatches();
    }
  }

  // The job's client and location names, for the summary.
  loadJobClient(job) {
    if (!job.client_id) return;
    Api.get(`/clients/${job.client_id}`)
      .then(action((response) => { this.jobClient = response.data.data; }))
      .catch(() => {});
  }

  findMatches() {
    const { attributes } = this.props;
    if (!attributes.email && !attributes.phone) {
      this.matches = [];
      return;
    }
    this.matches = null;
    Api.post('/clients/matches', { email: attributes.email, phone: attributes.phone })
      .then(action((response) => {
        this.matches = response.data.data || [];
        this.reasons = response.data.reasons || {};
      }))
      .catch(() => this.findMatchesBySearch());
  }

  // Before the backend has /clients/matches: the client search, keeping only
  // exact email matches (phone isn't comparable there). Its email field is
  // case-sensitive, so search the email as typed and in lowercase.
  findMatchesBySearch() {
    const typed = String(this.props.attributes.email || '').trim();
    const email = typed.toLowerCase();
    if (!email) {
      this.matches = [];
      return;
    }
    const terms = typed === email ? [email] : [typed, email];
    Promise.all(terms.map((term) => Api.post('/clients/search', { search: { term, per_page: 10, page: 1 } })
      .then((response) => response.data.data || [])
      .catch(() => [])))
      .then(action((results) => {
        const byId = {};
        results.flat().forEach((client) => {
          if (String(client.attributes.email || '').trim().toLowerCase() === email) byId[client.id] = client;
        });
        const found = Object.values(byId);
        this.matches = found;
        this.reasons = found.reduce((map, client) => ({ ...map, [client.id]: ['email'] }), {});
      }));
  }

  // --- client ---------------------------------------------------------------

  @action useClient(client) {
    this.client = client;
    this.locations = [];
    this.locationChoice = null;
    this.loadLocations(client);
  }

  loadLocations(client) {
    const { attributes } = this.props;
    this.busy = 'locations';
    Api.post('/locations/search', { search: { term: '*', where: { client_id: client.id }, per_page: 50, page: 1 } })
      .then(action((response) => {
        this.locations = response.data.data || [];
        const match = this.locations.find((location) => sameAddress(location, attributes));
        this.locationChoice = match ? match.id : (this.locations.length ? this.locations[0].id : NEW_LOCATION);
      }))
      .catch(action(() => {
        this.locations = [];
        this.locationChoice = NEW_LOCATION;
      }))
      .then(action(() => { this.busy = null; }));
  }

  createClient = () => {
    const { attributes } = this.props;
    const name = customerName(attributes) || attributes.email;
    this.busy = 'client';
    Api.post('/clients', {
      client: { name, legal_name: name, email: attributes.email, phone: attributes.phone },
    })
      .then((response) => {
        const client = response.data.data;
        Toast.show({ icon: 'tick', message: `Client ${client.attributes.name} created`, intent: Intent.SUCCESS });
        this.client = client;
        this.locations = [];
        this.locationChoice = NEW_LOCATION;
        if (this.bookingAddress) return this.createLocation();
        return null;
      })
      .catch((error) => this.showError(error, 'The client wasn\'t created.'))
      .then(action(() => { if (this.busy === 'client') this.busy = null; }));
  };

  @action changeClient = () => {
    this.client = null;
    this.locations = [];
    this.locationChoice = null;
    if (this.matches === null) this.findMatches();
  };

  // --- location -------------------------------------------------------------

  get bookingAddress() {
    const { attributes } = this.props;
    return attributes.address ? joinAddress(attributes.address, attributes.suite, attributes.city, attributes.state, attributes.zip) : '';
  }

  createLocation = () => {
    this.busy = 'location';
    return Api.post('/locations', { location: { full_address: this.bookingAddress, client_id: this.client.id } })
      .then(action((response) => {
        const location = response.data.data;
        this.locations = [...this.locations, location];
        this.locationChoice = location.id;
        Toast.show({ icon: 'tick', message: 'Booking address added as a location', intent: Intent.SUCCESS });
        return location;
      }))
      .catch((error) => this.showError(error, 'The location wasn\'t added.'))
      .then(action((result) => { this.busy = null; return result; }));
  };

  get chosenLocation() {
    return this.locations.find((location) => location.id === this.locationChoice) || null;
  }

  // --- job ------------------------------------------------------------------

  createJob = () => {
    if (this.locationChoice === NEW_LOCATION) {
      this.createLocation().then((location) => { if (location) this.openJobForm(location); });
    } else if (this.chosenLocation) {
      this.openJobForm(this.chosenLocation);
    }
  };

  @action openJobForm(location) {
    const { attributes, jobStore, store } = this.props;
    const { object } = jobStore.persistJob;
    const serviceName = (attributes.service_type && attributes.service_type.name) || 'Service';
    const name = customerName(attributes) || this.client.attributes.name;
    const start = attributes.start_date ? moment(attributes.start_date) : moment().add(1, 'day').startOf('hour').hour(9);

    object.client_id = this.client.id;
    object.client = this.client;
    object.location_id = location.id;
    object.location = location;
    object.title = `${serviceName} for ${name}`;
    object.reason_for_call = [
      `${serviceName} requested in booking ${bookingReference(attributes.id)}.`,
      attributes.note ? `Customer note: ${attributes.note}` : null,
    ].filter(Boolean).join('\n');
    object.service_type_id = attributes.service_type_id;
    object.selectedServiceType = attributes.service_type_id
      ? [{ value: attributes.service_type_id, selected: true, label: serviceName }]
      : [];
    object.start_date = start;
    object.end_date = moment(start).add(JOB_LENGTH_HOURS, 'hours');
    object.booking_id = attributes.id;
    jobStore.isServiceAgreement = false;
    store.isOpen = true;
  }

  showError(error, fallback) {
    const data = error && error.response && error.response.data;
    const message = (data && (data.errors || data.error)) || fallback;
    Toast.show({ icon: 'warning-sign', message: Array.isArray(message) ? message.join(', ') : String(message), intent: Intent.DANGER });
    return null;
  }

  // --- render ---------------------------------------------------------------

  renderJob(job) {
    const { onEditJob } = this.props;
    const client = this.jobClient;
    const location = client && (client.attributes.locations || []).find((item) => item.id === job.location_id);
    const start = moment(job.start_date);
    const end = moment(job.end_date);
    return (
      <section className="bk-client-job bk-client-job--done" aria-label="Job for this booking">
        <span className="bk-client-job__icon" aria-hidden="true"><Icon icon="tick" iconSize={16} /></span>
        <div className="bk-client-job__body">
          <h2 className="bk-client-job__title">Job #{job.event_number} is scheduled</h2>
          <p className="bk-client-job__when">
            {start.format('ddd, MMM D, YYYY')}, {start.format('h:mm A')} to {end.format(end.isSame(start, 'day') ? 'h:mm A' : 'ddd, MMM D, h:mm A')}
          </p>
          <p className="bk-client-job__text">
            {client ? client.attributes.name : 'Loading client'}{location ? `, ${location.full_address}` : ''}
          </p>
          <p className="bk-client-job__sync">
            <Icon icon="exchange" iconSize={12} /> The job and this booking keep the same date. Moving one moves the other.
          </p>
        </div>
        <div className="bk-client-job__actions">
          <AnchorButton className="bk-btn--tint" icon="share" href={`/dashboard/jobs/${job.id}`}>Open job</AnchorButton>
          {onEditJob && <Button icon="edit" onClick={onEditJob}>Edit job</Button>}
        </div>
      </section>
    );
  }

  renderMatches() {
    if (this.matches === null) {
      return <div className="bk-client-job__loading"><Spinner size={16} /> Looking for an existing client</div>;
    }
    if (this.matches.length === 0) {
      return <p className="bk-client-job__text">No client with this email or phone yet.</p>;
    }
    return (
      <ul className="bk-client-job__matches" aria-label="Matching clients">
        {this.matches.map((client) => (
          <li key={client.id} className="bk-client-job__match">
            <div className="bk-client-job__match-text">
              <span className="bk-client-job__name">{client.attributes.name}</span>
              <span className="bk-client-job__muted">{[client.attributes.email, client.attributes.phone].filter(Boolean).join(', ')}</span>
              <span className="bk-client-job__chips">
                {(this.reasons[client.id] || []).map((reason) => (
                  <span key={reason} className="bk-client-job__chip">{REASON_LABELS[reason] || reason}</span>
                ))}
              </span>
            </div>
            <Button className="bk-btn--tint" icon="tick" onClick={() => this.useClient(client)}>Use this client</Button>
          </li>
        ))}
      </ul>
    );
  }

  renderLocations() {
    if (this.busy === 'locations') {
      return <div className="bk-client-job__loading"><Spinner size={16} /> Loading locations</div>;
    }
    return (
      <div className="bk-client-job__locations" role="radiogroup" aria-label="Job location">
        {this.locations.map((location) => (
          <label key={location.id} className={`bk-client-job__location${this.locationChoice === location.id ? ' is-selected' : ''}`}>
            <input
              type="radio"
              name="bk-job-location"
              checked={this.locationChoice === location.id}
              onChange={() => { this.locationChoice = location.id; }}
            />
            <span>{location.attributes.full_address}</span>
            {sameAddress(location, this.props.attributes) && <span className="bk-client-job__chip">Booking address</span>}
          </label>
        ))}
        {this.bookingAddress && !this.locations.some((location) => sameAddress(location, this.props.attributes)) && (
          <label className={`bk-client-job__location${this.locationChoice === NEW_LOCATION ? ' is-selected' : ''}`}>
            <input
              type="radio"
              name="bk-job-location"
              checked={this.locationChoice === NEW_LOCATION}
              onChange={() => { this.locationChoice = NEW_LOCATION; }}
            />
            <span>Add the booking address: {this.bookingAddress}</span>
          </label>
        )}
        {!this.bookingAddress && this.locations.length === 0 && (
          <p className="bk-client-job__text">This client has no locations and the booking has no address. Add the address in section 2, save, then come back.</p>
        )}
      </div>
    );
  }

  render() {
    const { attributes } = this.props;
    if (!attributes || !attributes.id) return null;

    const job = bookingJob(attributes);
    if (job) return this.renderJob(job);

    const recurring = isRecurringBooking(attributes);
    const canCreateJob = Boolean(this.client) && (this.locationChoice === NEW_LOCATION ? Boolean(this.bookingAddress) : Boolean(this.chosenLocation));

    return (
      <section className="bk-client-job" aria-label="Client and job">
        <div className="bk-client-job__head">
          <h2 className="bk-client-job__title">Client and job</h2>
          <p className="bk-client-job__text">
            {recurring
              ? 'This is a recurring booking. Confirm it to set up a service agreement.'
              : 'Link this booking to a client, then create the job. The job and the booking keep the same date.'}
          </p>
        </div>

        {!recurring && (
          <ol className="bk-client-job__steps">
            <li className={`bk-client-job__step${this.client ? ' is-done' : ' is-current'}`}>
              <span className="bk-client-job__step-number" aria-hidden="true">{this.client ? <Icon icon="tick" iconSize={12} /> : 1}</span>
              <div className="bk-client-job__step-body">
                <h3 className="bk-client-job__step-title">Client</h3>
                {this.client ? (
                  <div className="bk-client-job__chosen">
                    <span className="bk-client-job__name">{this.client.attributes.name}</span>
                    <span className="bk-client-job__muted">{[this.client.attributes.email, this.client.attributes.phone].filter(Boolean).join(', ')}</span>
                    <Button minimal={true} small={true} onClick={this.changeClient}>Change</Button>
                  </div>
                ) : (
                  <React.Fragment>
                    {this.renderMatches()}
                    <div className="bk-client-job__row">
                      <Button icon="new-person" loading={this.busy === 'client'} onClick={this.createClient}>
                        {this.matches && this.matches.length ? 'Create a new client instead' : 'Create client from booking'}
                      </Button>
                      <span className="bk-client-job__muted">Uses {customerName(attributes) || 'the booking'}'s name, email, phone{this.bookingAddress ? ' and address' : ''}.</span>
                    </div>
                  </React.Fragment>
                )}
              </div>
            </li>

            <li className={`bk-client-job__step${!this.client ? ' is-upcoming' : canCreateJob ? ' is-done' : ' is-current'}`}>
              <span className="bk-client-job__step-number" aria-hidden="true">{this.client && canCreateJob ? <Icon icon="tick" iconSize={12} /> : 2}</span>
              <div className="bk-client-job__step-body">
                <h3 className="bk-client-job__step-title">Location</h3>
                {this.client && this.renderLocations()}
              </div>
            </li>

            <li className={`bk-client-job__step${canCreateJob ? ' is-current' : ' is-upcoming'}`}>
              <span className="bk-client-job__step-number" aria-hidden="true">3</span>
              <div className="bk-client-job__step-body">
                <h3 className="bk-client-job__step-title">Job</h3>
                {canCreateJob && (
                  <div className="bk-client-job__row">
                    <Button intent={Intent.PRIMARY} icon="calendar" loading={this.busy === 'location'} onClick={this.createJob}>
                      Create and schedule job
                    </Button>
                    <span className="bk-client-job__muted">
                      Opens the job form filled in from this booking
                      {attributes.start_date ? `, starting ${moment(attributes.start_date).format('ddd, MMM D, h:mm A')} for ${JOB_LENGTH_HOURS} hour` : ''}. Pick technicians, then save.
                    </span>
                  </div>
                )}
              </div>
            </li>
          </ol>
        )}
      </section>
    );
  }
}

export default BookingClientJob;
